import React, { useEffect, useState } from "react";
import { Card, Row, Col, Table, Typography, Divider } from "antd";
import { getsalesReturnRecordApi } from "../../../apiservice/sales";
import { numberFormat, convertNumberToWords, formatToCompanyTimezone, numberFormatTax } from "../../../lib/functions";
import "./voucher-mobile.css"
import { useSelector } from "react-redux";
import { selectAuthInfo } from "../../Auth/authSelectors";

const { Title, Text } = Typography;

const SalesReturnVoucherBody = ({ sale_r_id, printMode = false, showStamp = false, showSignature = false, setVoucherNo, setAccName, institution, setCustomerEmail }) => {
  const [data, setData] = useState(null);
  const authInfo = useSelector(selectAuthInfo);
  const currency = authInfo?.currency;

  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const isIndiaGst = authInfo?.gst_enabled === "yes";
  const taxLabel = isIndia ? "GST%" : "VAT%";
  const showHSN = isIndia && isIndiaGst;
  const isGSTDisabled =
    isIndia && authInfo?.gst_enabled == "no";

  useEffect(() => {
    if (!sale_r_id) return;
    getsalesReturnRecordApi({ sale_r_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) {
        const record = res.data[0];
        setData(record);

        // important guard for printMode
        if (!printMode && setVoucherNo) {
          setVoucherNo(record.sale_r_voucher_no);
          setAccName(record.acc_name);
        }

        if (record.email_address) {
          setCustomerEmail?.(record.email_address);
        }
      }
    });
  }, [sale_r_id]);

  if (!data) return null;

  const isIndividual = data.is_cal_type === "individual";
  const items = data.details || [];

  // calculate grand total
  const totals = items.reduce(
    (acc, d) => {
      const qty = d.sale_r_qty || d.item_qty || 0;
      const rate = Number(d.item_rate || 0);

      let base = qty * rate;

      const discountAmt =
        (Number(d.item_discount_per || 0) / 100) * base;

      const afterDiscount = base - discountAmt;

      const taxAmt =
        (Number(d.item_tax_per || 0) / 100) * afterDiscount;

      acc.subTotal += base;
      acc.discountTotal += discountAmt;
      acc.taxTotal += taxAmt;
      acc.grandTotal += afterDiscount + taxAmt;

      return acc;
    },
    { subTotal: 0, discountTotal: 0, taxTotal: 0, grandTotal: 0 }
  );

  const grandTotal = isIndividual
    ? totals.grandTotal
    : data?.total_amount || 0;

  const totalTax = isIndividual
    ? totals.taxTotal
    : Number(data?.total_tax || 0);

  const cgst = isIndia ? totalTax / 2 : 0;
  const sgst = isIndia ? totalTax / 2 : 0;


  const columns = isIndividual
    ? [
      { title: "#", render: (_, __, i) => i + 1, width: 40 },
      {
        title: "Item Name",
        render: (_, record) => (
          <>
            <div>{record.item_name}</div>

            {record.batch_name && (
              <div
                style={{
                  fontSize: 11,
                  color: "#888",
                  marginTop: 2,
                }}
              >
                Batch: {record.batch_name}
              </div>
            )}

            {record.item_description && (
              <div
                style={{
                  fontSize: 11,
                  color: "#666",
                  marginTop: 4,
                  whiteSpace: "pre-wrap",
                }}
              >
                {record.item_description}
              </div>
            )}
          </>
        ),
      },
      ...(showHSN
        ? [
          {
            title: "HSN/SAC",
            dataIndex: "hsn_sac_code",
            align: "center",
            width: 100,
          },
        ]
        : []),
      {
        title: "Rate",
        dataIndex: "item_rate",
        align: "right",
        render: (v) => Number(v).toFixed(2),
        width: 70
      },
      { title: "Per", dataIndex: "per_unit_symbol", align: "right", width: 70 },
      {
        title: "Quantity",
        dataIndex: "sale_r_qty",
        align: "right", width: 80,
        render: (_, d) => `${d.sale_r_qty} ${d.unit_symbol}`,
      },
      { title: "Disct%", dataIndex: "item_discount_per", align: "right", width: 75 },
      ...(!isGSTDisabled
        ? [
          { title: `${taxLabel} `, dataIndex: "item_tax_per", align: "right", width: 70 },
        ]
        : []),

      {
        title: "Total",
        align: "right",
        render: (_, d) => {
          let qty = d.item_qty || d.sale_r_qty || 0;
          let rate = Number(d.item_rate || 0);
          let total = qty * rate;
          total -= (Number(d.item_discount_per) / 100) * total;
          total += (Number(d.item_tax_per) / 100) * total;
          return total.toFixed(2);
        },
        width: 90
      },
    ]
    : [
      { title: "#", render: (_, __, i) => i + 1, width: 40 },
      {
        title: "Item Name",
        render: (_, record) => (
          <>
            <div>{record.item_name}</div>
            {record.batch_name && (
              <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                Batch: {record.batch_name}
              </div>
            )}
          </>
        ),
      },
      ...(showHSN
        ? [
          {
            title: "HSN/SAC",
            dataIndex: "hsn_sac_code",
            align: "center",
            width: 100,
          },
        ]
        : []),
      {
        title: "Rate",
        dataIndex: "item_rate",
        align: "right",
        render: (v) => Number(v).toFixed(2),
        width: 70
      },
      {
        title: "Quantity",
        // dataIndex: "item_qty", 
        align: "right", width: 110,
        render: (_, d) => `${d.item_qty} ${d?.unit_symbol}`,
      },
      {
        title: "Amount",
        dataIndex: "item_total",
        align: "right",
        render: (v) => Number(v).toFixed(2),
        width: 90
      },
    ];

  return (
    <div className="voucher-section invoice-v2">
      {/* Header */}

      <Row justify="space-between" align="top" className="invoice-header">
        <Col flex="0 0 320px" className="bill-to" style={{ marginTop: "58px" }}>
          <Text strong>Customer / Debitor Code: &nbsp;{data.acc_code}</Text>
          <br />
          <Text strong>Name: &nbsp;{data.acc_name}</Text>
          {/* <br />
          <Text >Institution: &nbsp;{data.institution_name}</Text> */}
          <br />
          <Text className="wrap-text">Address: &nbsp; {data.address}</Text>

          <Text>Contact No: &nbsp; +{data.contact_no}</Text>
          <br />
          {data?.acc_mgr && <Text className="wrap-text">
            {institution?.country?.trim().toLowerCase() === "united arab emirates" &&
              (institution?.subscription_type.toLowerCase().includes("pharma") || institution?.subscription_type.toLowerCase().includes("pharmabooks"))
              ? "Rep. : "
              : "Account Manager : "}{" "}
            {data.acc_mgr && data.acc_mgr}
          </Text>}
          {data?.customer_po_ref && <><Text>Customer Po Ref : {data.customer_po_ref}</Text>
            <br /></>}
          {data?.payment_terms && <><Text>Payment Terms : {data.payment_terms} Days</Text>
            <br />
          </>
          }
        </Col>
        <Col className="invoice-title">
          <Title level={2}>SALES RETURN</Title>
          <Text>Invoice No: {data.sale_r_voucher_no}</Text>
          <br />
          <Text>Return Date: {formatToCompanyTimezone(data.created_date)?.split(" ")[0]}</Text>
          <br />
          <Text>Return By: {data.full_name?.split(" ")[0]}</Text>
          <br />
          <Text className="wrap-text">Address : {institution?.street_address}</Text>
          {institution?.phone_no && <Text>Contact No : &nbsp; +{institution?.phone_no}</Text>}
          <br />

          {institution?.vat_license && <><Text>
            {institution?.country?.trim().toLowerCase() === "united arab emirates"
              ? "TRN No: "
              : "Tax Reg No: "}{" "}
            {institution?.vat_license}
          </Text><br /></>}
          {data.licence_no && <Text>License No : {data.licence_no}</Text>}
        </Col>

      </Row>


      {/* Items Table */}
      <Table
        className="invoice-items"
        dataSource={items}
        columns={columns}
        pagination={false}
        rowKey="sale_r_d_id"
        size="small"
        summary={() => {
          if (isIndividual) {
            return (
              <Table.Summary.Row>
                {/* <Table.Summary.Cell colSpan={5} align="right">
                  <b>Grand Total</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <b>{grandTotal.toFixed(2)}</b>
                </Table.Summary.Cell> */}
              </Table.Summary.Row>
            );
          }
          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={3} align="right">
                  Subtotal
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {Number(data.sub_total || 0).toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={3} align="right">
                  Discount
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {Number(data.total_discount || 0).toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              {isIndiaGst ? (
                <>
                  {data.place_of_supply_type == 'intra' && <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={3} align="right">
                        CGST
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        {numberFormatTax(data.cgst_amount)}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>

                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={3} align="right">
                        SGST
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        {numberFormatTax(data.sgst_amount)}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>}

                  {data.place_of_supply_type == 'inter' &&
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={3} align="right">
                          IGST
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          {numberFormatTax(data.igst_amount)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </>}


                  {data.gst_calculated == 'no' &&
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={3} align="right">
                          {taxLabel}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          {totalTax.toFixed(2)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </>}
                </>
              ) : (
                !isIndia && (
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={3} align="right">
                      {taxLabel}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      {totalTax.toFixed(2)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                ))}

              {/* <Table.Summary.Row>
                <Table.Summary.Cell colSpan={3} align="right">
                  <b>Grand Total</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <b>{Number(grandTotal).toFixed(2)}</b>
                </Table.Summary.Cell>
              </Table.Summary.Row> */}
            </>
          );
        }}
      />

      {/* Footer */}
      <Divider />
      <Row>
        <Col span={12}>
          {/* <Text>Invoice Due : {numberFormat(data.due_total || 0)}</Text>
    <br />
    <Text>
      Current Customer Due : {numberFormat(data.due_amount || 0)}
    </Text> */}
        </Col>

        <Col span={12} style={{ textAlign: "right" }}>
          <div>
            {/* Quantity */}
            {/* <div style={{ display: "flex",  textAlign: "left" }}>
        <Text>Quantity Total :</Text>
        <Text>
          ({items.reduce((sum, d) => sum + (Number(d.item_qty) || 0), 0)})
        </Text>
      </div> */}

            {/* Sub Total */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>Sub Total : </Text>
              <Text>
                {numberFormat(isIndividual ? totals.subTotal : data.sub_total || 0)}
              </Text>
            </div>
            {isIndiaGst ? (
              <>
                {data.place_of_supply_type == 'intra' && <>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text>CGST :</Text>
                    <Text>{numberFormatTax(data.cgst_amount)}</Text>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text>SGST :</Text>
                    <Text>{numberFormatTax(data.sgst_amount)}</Text>
                  </div>
                </>}

                {data.place_of_supply_type == 'inter' &&
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <Text>IGST :</Text>
                      <Text>{numberFormatTax(data.igst_amount)}</Text>
                    </div>
                  </>}

                {data.gst_calculated == 'no' &&
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <Text>{taxLabel} :</Text>
                      <Text>{numberFormat(totalTax)}</Text>
                    </div>
                  </>}
              </>
            ) : (
              !isIndia && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text>{taxLabel} :</Text>
                  <Text>{numberFormat(totalTax)}</Text>
                </div>
              ))}

            {/* Grand Total */}
            <div
              style={{
                borderTop: "1px solid #000",
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 4,
                marginTop: 4,
              }}
            >
              <Text strong>Grand Total ({currency}) : </Text>
              <Text strong>{numberFormat(data.total_amount)}</Text>
            </div>

            {/* Paid */}
            {/* <div
        style={{
          borderTop: "1px solid #000",         
          display: "flex",
          justifyContent: "space-between",
          paddingTop: 4,
          marginTop: 4,
        }}
      >
        <Text strong>Paid : INR</Text>
        <Text strong>{numberFormat(data.paid_total || 0)}</Text>
      </div> */}

            {/* Due */}
            {/* <div
        style={{          
          display: "flex",
          justifyContent: "space-between",
          paddingTop: 2,          
        }}
      >
        <Text strong>Due : INR</Text>
        <Text strong>{numberFormat(data.due_total || 0)}</Text>
      </div> */}
          </div>
        </Col>
      </Row>

      <Divider />
      <Row>
        <Col span={24}>
          <Text>
            In Word of Grand Total :{" "}
            {convertNumberToWords(data.total_amount, data?.currency || currency)}
          </Text>
          <br />
          <Text>Description : {data.narration || "-"}</Text>
        </Col>
      </Row>

      {(showStamp || showSignature) && (
        <div className="invoice-footer-section">
          <div className="footer-right-group">
            {showStamp && institution?.stamp && (
              <div className="stamp-block">
                <img src={institution.stamp} alt="Stamp" />
              </div>
            )}

            {showSignature && institution?.signature && (
              <div className="signature-block">
                <img src={institution.signature} alt="Signature" />
                <div className="sign-line-text">Authorized Signature</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesReturnVoucherBody;
